/**
 * OTP verification route — confirms passwordless login via a 6-digit code.
 *
 * Styled after the bndl design pattern: centered card with an `InputOTP`
 * component (6 individual slots), a primary verify button, and a resend
 * button with a countdown timer.
 *
 * If no email is present in the URL search params, redirects back to
 * `/login` to start the flow from scratch.
 *
 * On successful verification the user's scopes are resolved from their
 * org memberships and class enrollments, then the user is redirected to:
 *
 * 1. The `returnTo` URL search param (if present and same-origin).
 * 2. The default zone for their resolved scopes (admin → student → home).
 *
 * @module routes/verify
 */

import { saveAccount } from '@learnai/api/accounts';
import { defaultRedirectForScopes } from '@learnai/api/auth';
import { useAuth } from '@learnai/api/auth-provider';
import { storage } from '@learnai/api/storage';
import { tryCatch } from '@learnai/tools/try-catch';
import { Button } from '@learnai/ui/components/ui/button';
import { Card, CardContent } from '@learnai/ui/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@learnai/ui/components/ui/input-otp';
import { toast } from '@learnai/ui/components/ui/sonner';
import { Spinner } from '@learnai/ui/components/ui/spinner';
import { api } from '@web/lib/api';
import { resolvePostLoginScopes, safeReturnTo } from '@web/lib/auth-helpers';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

// ---------------------------------------------------------------------------
// Countdown timer hook
// ---------------------------------------------------------------------------

function useCountdown(initialSeconds: number) {
	const [remaining, setRemaining] = useState(() => {
		const stored = sessionStorage.getItem('otp-timer');
		return stored ? Number(stored) : 0;
	});
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

	function start(seconds: number = initialSeconds) {
		// Clear any existing timer before starting a new one.
		if (timerRef.current) clearInterval(timerRef.current);

		setRemaining(seconds);
		timerRef.current = setInterval(() => {
			setRemaining((prev) => {
				const next = prev - 1;
				if (next < 1) {
					if (timerRef.current) clearInterval(timerRef.current);
					timerRef.current = null;
				}
				return next;
			});
		}, 1000);
	}

	// Restore a running timer on mount.
	useEffect(() => {
		if (remaining > 0) {
			start(remaining);
		}
		return () => {
			if (timerRef.current) clearInterval(timerRef.current);
		};
		// Only run on mount.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Persist the timer value so it survives page refreshes.
	useEffect(() => {
		if (remaining > 0) {
			sessionStorage.setItem('otp-timer', remaining.toString());
		} else {
			sessionStorage.removeItem('otp-timer');
		}
	}, [remaining]);

	return { remaining, start, isActive: remaining > 0 };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Verify() {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { isAuthenticated, isLoading: authLoading, scopes } = useAuth();

	const emailParam = searchParams.get('email') ?? '';
	const returnTo = safeReturnTo(searchParams.get('returnTo'));

	const [code, setCode] = useState('');
	const [loading, setLoading] = useState(false);

	const countdown = useCountdown(60);

	// If no email was provided, redirect back to the login page.
	useEffect(() => {
		if (!emailParam) {
			navigate('/login', { replace: true });
		}
	}, [emailParam, navigate]);

	// Redirect if the user is already authenticated.
	useEffect(() => {
		if (!authLoading && isAuthenticated) {
			const destination = returnTo ?? defaultRedirectForScopes(scopes);
			window.location.href = destination;
		}
	}, [authLoading, isAuthenticated, scopes, returnTo]);

	// -----------------------------------------------------------------------
	// Handlers
	// -----------------------------------------------------------------------

	async function verifyCode(value: string) {
		if (loading) return;
		setLoading(true);

		try {
			const result = await api.auth.otp.verify({ email: emailParam, code: value });

			// Persist tokens and current session id so the API client's
			// refresh interceptor and the auth provider can find them.
			await storage.local.set('accessToken', result.tokens.accessToken);
			await storage.local.set('refreshToken', result.tokens.refreshToken);
			if (result.tokens.sessionId) {
				await storage.local.set('currentSessionId', result.tokens.sessionId);
			}

			// Snapshot the newly authenticated account into the multi-
			// account registry so it appears in the quick-login list on
			// future visits to the login page.
			await saveAccount({
				id: result.user.id,
				email: result.user.email,
				name: result.user.name,
				avatarUrl: result.user.avatarUrl,
				refreshToken: result.tokens.refreshToken,
			});

			// Clean up the countdown timer.
			sessionStorage.removeItem('otp-timer');

			// Resolve where to send the user based on their scopes.
			if (returnTo) {
				window.location.href = returnTo;
			} else {
				const destination = await resolvePostLoginScopes(result.tokens.accessToken);
				window.location.href = destination;
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Invalid or expired code. Please try again.');
			setLoading(false);
		}
	}

	async function handleSubmit(e: React.SyntheticEvent<HTMLFormElement, SubmitEvent>) {
		e.preventDefault();
		e.stopPropagation();
		verifyCode(code);
	}

	async function handleResend() {
		if (countdown.isActive) return;

		countdown.start(60);

		const { error: sendError } = await tryCatch(api.auth.otp.send({ email: emailParam }));

		if (sendError) {
			toast.error(sendError instanceof Error ? sendError.message : 'Failed to resend code. Please try again.');
		}
	}

	// -----------------------------------------------------------------------
	// Guards
	// -----------------------------------------------------------------------

	if (!emailParam) {
		return null;
	}

	if (authLoading || isAuthenticated) {
		return (
			<div className="flex h-screen w-full items-center justify-center">
				<Spinner className="size-6 text-muted-foreground" />
			</div>
		);
	}

	// -----------------------------------------------------------------------
	// Render
	// -----------------------------------------------------------------------

	return (
		<div className="flex h-screen w-full items-center justify-center px-4">
			<Card className="mx-4 max-w-md overflow-hidden">
				<CardContent className="grid p-0 md:grid-cols-1">
					<form className="p-6 md:p-8" onSubmit={handleSubmit}>
						<div className="flex flex-col items-center gap-6">
							<div className="flex flex-col items-center text-center">
								<h1 className="text-2xl font-bold">Confirm Login</h1>
								<p className="text-balance text-muted-foreground">A one time password has been sent to {emailParam}.</p>
							</div>

							<div className="grid gap-2">
								<InputOTP
									autoFocus
									maxLength={6}
									value={code}
									onChange={(value) => setCode(value)}
									onComplete={verifyCode}
								>
									<InputOTPGroup>
										<InputOTPSlot index={0} className="h-14 w-10 text-xl font-semibold bg-background" />
										<InputOTPSlot index={1} className="h-14 w-10 text-xl font-semibold bg-background" />
										<InputOTPSlot index={2} className="h-14 w-10 text-xl font-semibold bg-background" />
										<InputOTPSlot index={3} className="h-14 w-10 text-xl font-semibold bg-background" />
										<InputOTPSlot index={4} className="h-14 w-10 text-xl font-semibold bg-background" />
										<InputOTPSlot index={5} className="h-14 w-10 text-xl font-semibold bg-background" />
									</InputOTPGroup>
								</InputOTP>
							</div>

							<div className="flex w-full flex-col gap-3">
								<Button
									variant="outline"
									type="button"
									className="w-full"
									disabled={countdown.isActive}
									onClick={handleResend}
								>
									Send Again{countdown.isActive ? ` ${countdown.remaining}` : ''}
								</Button>
							</div>
						</div>
					</form>
				</CardContent>
			</Card>
		</div>
	);
}
